import { useQuery } from '@tanstack/react-query';
import React from 'react';
import {
  AllianceIdentity,
  AlliancePerson,
} from '../components/alliance/alliance-identity';
import { AppContext } from '../contexts/app-context';
import { AuthContext } from '../contexts/auth-context';
import { AgentSelect, AuthorizeAgentData } from '../types';
import { AUTHORIZE_QUERY_KEY, fetchAuthorize } from '../utils/authorize';
import { captureError } from '../utils/capture-error';
import { isAgt } from '../utils/is-agent';

/** "First Last" from a record's name fields, or null when both are empty */
const joinName = (params: {
  first?: string | null;
  last?: string | null;
  fallback?: string | null;
}) => {
  const name = [params.first, params.last].filter(Boolean).join(' ').trim();
  return name || params.fallback || null;
};

/** An /authorize Agent record as an AlliancePerson */
const authorizeAgentToPerson = (params: {
  agent: AuthorizeAgentData;
}): AlliancePerson => {
  // Params
  const { agent } = params;
  const id = agent.AgtNo || '';
  return {
    fullName:
      joinName({
        first: agent.Fname,
        last: agent.Lname,
        fallback: agent.AgtName,
      }) || id,
    id,
    email: agent.Email,
  };
};

/** ARC's impersonated agent (lowercase /agent/search row) as an AlliancePerson */
const impersonatedToPerson = (params: {
  agent: AgentSelect;
}): AlliancePerson => {
  // Params
  const { agent } = params;
  return {
    fullName:
      joinName({
        first: agent.fname,
        last: agent.lname,
        fallback: agent.agentname,
      }) || agent.agentno,
    id: agent.agentno,
  };
};

/**
 * The Contracting API's /authorize for the signed-in user (Agent or Staff
 * record and IMO). Dashboard uses it for display (user control, user menu,
 * System Screen identity chips); access is still decided by the Dashboard
 * API's /authorize in require-auth.tsx. It also supplies Agent.HasTecCC for
 * the onboarding screen; a failure here never blocks anyone.
 */
export function useAuthorizeQuery() {
  // Context
  const { state } = React.useContext(AuthContext);

  return useQuery({
    enabled: state.user !== undefined,
    queryKey: AUTHORIZE_QUERY_KEY,
    queryFn: fetchAuthorize,
    staleTime: Infinity,
    // A failed /authorize must not refetch each time another reader mounts:
    // v4 resets an errored query with no data to `loading`, the gate goes
    // back to the splash and unmounts the app, and the loop repeats about
    // once a second. A failure keeps the token identity for the session.
    retryOnMount: false,
    onError: (error) =>
      captureError({
        data: {
          error,
          request: { body: {}, path: 'ContractingAPI /authorize' },
        },
      }),
  });
}

/**
 * Builds the normalized AllianceIdentity for the user control and System
 * Screens from Dashboard's auth state.
 *
 * - Staff: `myStaffForAgt` on the access token. The staff member's name comes
 *   from /authorize Staff; the agent's name from /authorize Agent when it is
 *   that agent.
 * - Agent: /authorize returned an Agent record, or (when /authorize is
 *   unavailable) the username is an agent number.
 * - Admin: everyone else. The name comes from /authorize Staff (the
 *   employee record admins get), then the ID token, then the username.
 *
 * Returns null until the Cognito user is known.
 */
export function useAllianceIdentity(): AllianceIdentity | null {
  // Context
  const { state } = React.useContext(AuthContext);
  const {
    state: { impersonatedAgent },
  } = React.useContext(AppContext);
  // Hooks
  const queryAuthorize = useAuthorizeQuery();

  const { user, session } = state;
  const authorize = queryAuthorize.data;

  return React.useMemo(() => {
    if (!user) {
      return null;
    }

    const username: string = user.getUsername();
    const idToken = session?.getIdToken().decodePayload() || {};
    const staffAgtNo: string | undefined = session
      ?.getAccessToken()
      .decodePayload()['myStaffForAgt'];
    const tokenName = joinName({
      first: idToken['given_name'],
      last: idToken['family_name'],
      fallback: idToken['name'],
    });
    const tokenEmail: string | null = idToken['email'] || null;

    const impersonating = impersonatedAgent
      ? impersonatedToPerson({ agent: impersonatedAgent })
      : null;

    if (staffAgtNo) {
      const staffName = joinName({
        first: authorize?.Staff?.Fname,
        last: authorize?.Staff?.Lname,
      });
      const agent =
        authorize?.Agent && authorize.Agent.AgtNo === staffAgtNo
          ? authorizeAgentToPerson({ agent: authorize.Agent })
          : { fullName: staffAgtNo, id: staffAgtNo };
      return {
        role: 'staff',
        user: {
          fullName: staffName || tokenName || username,
          id: authorize?.Staff?.Username || username,
          email: tokenEmail,
        },
        staffFor: agent,
        impersonating,
      };
    }

    if (authorize?.Agent) {
      const agent = authorizeAgentToPerson({ agent: authorize.Agent });
      return {
        role: 'agent',
        user: { ...agent, email: agent.email || tokenEmail },
        staffFor: null,
        impersonating,
      };
    }

    // /authorize failed or hasn't answered yet: an agent number username is
    // still an agent, shown by number until a name is known
    if (!authorize && isAgt(username)) {
      return {
        role: 'agent',
        user: {
          fullName: tokenName || username,
          id: username,
          email: tokenEmail,
        },
        staffFor: null,
        impersonating,
      };
    }

    // Admins: /authorize returns their employee record in Staff (no
    // myStaffForAgt), which carries their name
    const employeeName = joinName({
      first: authorize?.Staff?.Fname,
      last: authorize?.Staff?.Lname,
    });
    return {
      role: 'admin',
      user: {
        fullName: employeeName || tokenName || username,
        id: username,
        email: tokenEmail,
      },
      staffFor: null,
      impersonating,
    };
  }, [user, session, authorize, impersonatedAgent]);
}
